/**
 * Country-based language suggestion.
 *
 * Decides whether to offer the visitor a different storefront language and, if
 * so, fills in and reveals #language-suggestion. Every string in the box is
 * written in the language being offered.
 *
 * Country detection order (no third party services are contacted):
 *   1. Shopify's own /browsing_context_suggestions endpoint (same origin)
 *   2. navigator.languages
 *   3. the browser time zone
 *
 * Once the visitor dismisses the box or makes a choice, it is not shown again
 * (remembered in localStorage).
 */

const STORAGE_KEY = 'bass:language-suggestion:dismissed';

/** Copy for each language we can offer, written in that language. */
const COPY = {
  en: {
    question: 'Would you like to continue in English?',
    cta: 'Switch to English',
    stay: 'Continue in {lang}',
    close: 'Close',
  },
  de: {
    question: 'Möchten Sie auf Deutsch fortfahren?',
    cta: 'Zu Deutsch wechseln',
    stay: 'Weiter auf {lang}',
    close: 'Schließen',
  },
  fr: {
    question: 'Souhaitez-vous continuer en français ?',
    cta: 'Passer en français',
    stay: 'Continuer en {lang}',
    close: 'Fermer',
  },
  es: {
    question: '¿Quieres continuar en español?',
    cta: 'Cambiar a español',
    stay: 'Continuar en {lang}',
    close: 'Cerrar',
  },
  it: {
    question: 'Vuoi continuare in italiano?',
    cta: 'Passa all’italiano',
    stay: 'Continua in {lang}',
    close: 'Chiudi',
  },
  ru: {
    question: 'Хотите продолжить на русском?',
    cta: 'Перейти на русский',
    stay: 'Продолжить: {lang}',
    close: 'Закрыть',
  },
  'pt-PT': {
    question: 'Deseja continuar em português?',
    cta: 'Mudar para português',
    stay: 'Continuar em {lang}',
    close: 'Fechar',
  },
  nl: {
    question: 'Wilt u doorgaan in het Nederlands?',
    cta: 'Overschakelen naar Nederlands',
    stay: 'Doorgaan in {lang}',
    close: 'Sluiten',
  },
  pl: {
    question: 'Czy chcesz kontynuować po polsku?',
    cta: 'Przełącz na polski',
    stay: 'Kontynuuj: {lang}',
    close: 'Zamknij',
  },
  ar: {
    question: 'هل تريد المتابعة بالعربية؟',
    cta: 'التبديل إلى العربية',
    stay: 'المتابعة باللغة {lang}',
    close: 'إغلاق',
  },
  tr: {
    question: 'Türkçe devam etmek ister misiniz?',
    cta: 'Türkçe’ye geç',
    stay: '{lang} devam et',
    close: 'Kapat',
  },
};

/**
 * Country -> candidate languages, most likely first.
 * Countries that are not listed fall back to English.
 */
const COUNTRY_LANGUAGES = {
  TR: ['tr'],
  DE: ['de'], AT: ['de'], CH: ['de', 'fr', 'it'], LI: ['de'],
  FR: ['fr'], MC: ['fr'], LU: ['fr', 'de'], SN: ['fr'], CI: ['fr'], CM: ['fr'],
  BE: ['nl', 'fr'], NL: ['nl'], SR: ['nl'], AW: ['nl'], CW: ['nl'], SX: ['nl'],
  ES: ['es'], MX: ['es'], AR: ['es'], CO: ['es'], CL: ['es'], PE: ['es'],
  VE: ['es'], EC: ['es'], GT: ['es'], CU: ['es'], BO: ['es'], DO: ['es'],
  HN: ['es'], PY: ['es'], SV: ['es'], NI: ['es'], CR: ['es'], PA: ['es'],
  UY: ['es'], GQ: ['es'],
  IT: ['it'], SM: ['it'], VA: ['it'],
  RU: ['ru'], BY: ['ru'], KZ: ['ru'], KG: ['ru'], TJ: ['ru'], UZ: ['ru'],
  PT: ['pt-PT'], BR: ['pt-PT'], AO: ['pt-PT'], MZ: ['pt-PT'], CV: ['pt-PT'],
  GW: ['pt-PT'], ST: ['pt-PT'], TL: ['pt-PT'],
  PL: ['pl'],
  SA: ['ar'], AE: ['ar'], EG: ['ar'], QA: ['ar'], KW: ['ar'], BH: ['ar'],
  OM: ['ar'], JO: ['ar'], LB: ['ar'], IQ: ['ar'], LY: ['ar'], DZ: ['ar'],
  MA: ['ar'], TN: ['ar'], SD: ['ar'], YE: ['ar'], SY: ['ar'], PS: ['ar'],
  MR: ['ar'], SO: ['ar'], DJ: ['ar'], KM: ['ar'],
  US: ['en'], GB: ['en'], IE: ['en'], AU: ['en'], NZ: ['en'], CA: ['en', 'fr'],
  ZA: ['en'], IN: ['en'], SG: ['en'], PH: ['en'], NG: ['en'], KE: ['en'],
  GH: ['en'], MY: ['en'], PK: ['en'], HK: ['en'], MT: ['en'],
};

/** Rough time zone -> country, used only when everything else fails. */
const TIMEZONE_COUNTRIES = {
  'Europe/Istanbul': 'TR',
  'Europe/Berlin': 'DE', 'Europe/Vienna': 'AT', 'Europe/Zurich': 'CH',
  'Europe/Paris': 'FR', 'Europe/Brussels': 'BE', 'Europe/Amsterdam': 'NL',
  'Europe/Madrid': 'ES', 'Europe/Rome': 'IT', 'Europe/Lisbon': 'PT',
  'Europe/Warsaw': 'PL', 'Europe/Moscow': 'RU', 'Europe/Minsk': 'BY',
  'Europe/London': 'GB', 'Europe/Dublin': 'IE',
  'America/New_York': 'US', 'America/Chicago': 'US', 'America/Denver': 'US',
  'America/Los_Angeles': 'US', 'America/Toronto': 'CA',
  'America/Mexico_City': 'MX', 'America/Sao_Paulo': 'BR',
  'Asia/Dubai': 'AE', 'Asia/Riyadh': 'SA', 'Africa/Cairo': 'EG',
  'Australia/Sydney': 'AU',
};

/** @param {string} tag @returns {string} the base language of a locale tag */
const base = (tag) => String(tag || '').toLowerCase().split('-')[0];

/**
 * Picks the published locale that best matches a language tag.
 *
 * @param {string} tag
 * @param {string[]} published
 * @returns {string | undefined}
 */
function matchPublished(tag, published) {
  const exact = published.find((code) => code.toLowerCase() === String(tag).toLowerCase());
  if (exact) return exact;
  return published.find((code) => base(code) === base(tag));
}

/**
 * Asks Shopify which country the visitor appears to be browsing from.
 *
 * @param {string} root
 * @returns {Promise<string | undefined>}
 */
async function detectCountry(root) {
  const url = `${root.replace(/\/$/, '')}/browsing_context_suggestions?country[enabled]=true&language[enabled]=true`;

  try {
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) return undefined;

    const data = await response.json();
    const handle = data?.detected_values?.country?.handle;
    if (handle) return String(handle).toUpperCase();
  } catch {
    // Fall through to the browser-based guesses below.
  }

  return undefined;
}

/**
 * Works out which language to offer, if any.
 *
 * @param {string} current
 * @param {string[]} published
 * @param {string | undefined} country
 * @returns {string | undefined}
 */
function chooseLanguage(current, published, country) {
  const browser = (navigator.languages || [navigator.language || '']).filter(Boolean);

  /** @type {string[]} */
  let candidates = [];

  if (country && COUNTRY_LANGUAGES[country]) {
    candidates = COUNTRY_LANGUAGES[country].slice();
  } else if (country) {
    candidates = ['en'];
  }

  // When a country maps to more than one language, let the browser break the tie.
  if (candidates.length > 1) {
    const preferred = candidates.find((code) => browser.some((tag) => base(tag) === base(code)));
    if (preferred) candidates = [preferred, ...candidates.filter((c) => c !== preferred)];
  }

  // No country: fall back to the browser languages, then the time zone.
  if (candidates.length === 0) {
    candidates = browser.slice();

    if (candidates.length === 0) {
      try {
        const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const zoneCountry = TIMEZONE_COUNTRIES[zone];
        if (zoneCountry) candidates = COUNTRY_LANGUAGES[zoneCountry] || ['en'];
      } catch {
        // No time zone available; give up quietly.
      }
    }
  }

  for (const candidate of candidates) {
    // The visitor's own language is the one they are already reading: say nothing.
    if (base(candidate) === base(current)) return undefined;

    const match = matchPublished(candidate, published);
    if (match) return match;
  }

  // None of the visitor's languages are published yet (this is the normal case
  // while languages are still being rolled out). English is the safest offer.
  const english = matchPublished('en', published);
  if (english && base(english) !== base(current)) return english;

  return undefined;
}

/**
 * Returns the name of `locale` written in `inLocale`, falling back to the tag.
 *
 * @param {string} locale
 * @param {string} inLocale
 * @returns {string}
 */
function languageName(locale, inLocale) {
  try {
    const names = new Intl.DisplayNames([inLocale], { type: 'language' });
    const name = names.of(locale);
    if (name) return name.charAt(0).toUpperCase() + name.slice(1);
  } catch {
    // Intl.DisplayNames is unavailable or does not know this locale.
  }
  return locale.toUpperCase();
}

async function init() {
  const box = document.getElementById('language-suggestion');
  if (!(box instanceof HTMLElement)) return;

  try {
    if (localStorage.getItem(STORAGE_KEY)) return;
  } catch {
    // Storage is blocked; carry on and simply show the box this once.
  }

  const current = box.dataset.currentLocale || '';
  const published = (box.dataset.publishedLocales || '').split(',').filter(Boolean);
  const root = box.dataset.rootUrl || '/';
  if (published.length < 2) return;

  const country = await detectCountry(root);
  const target = chooseLanguage(current, published, country);
  if (!target) return;

  const copy = COPY[target] || COPY[base(target)];
  if (!copy) return;

  const question = box.querySelector('[data-role="question"]');
  const switchButton = box.querySelector('[data-role="switch"]');
  const stayButton = box.querySelector('[data-role="stay"]');
  const closeButton = box.querySelector('[data-role="dismiss"]');
  if (!question || !(switchButton instanceof HTMLButtonElement) || !stayButton || !closeButton) return;

  const remember = () => {
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // Nothing we can do; the box may appear again on the next visit.
    }
  };

  box.setAttribute('lang', target);
  box.setAttribute('dir', base(target) === 'ar' ? 'rtl' : 'ltr');
  box.setAttribute('aria-label', copy.question);

  question.textContent = copy.question;
  switchButton.textContent = copy.cta;
  switchButton.value = target;
  stayButton.textContent = copy.stay.replace('{lang}', languageName(current, target));
  closeButton.setAttribute('aria-label', copy.close);

  // Any of the three actions means "do not ask again".
  switchButton.addEventListener('click', remember);
  for (const button of [stayButton, closeButton]) {
    button.addEventListener('click', () => {
      remember();
      box.hidden = true;
    });
  }

  box.hidden = false;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
